import { useMutation, useQueryClient } from '@tanstack/react-query';
import { attachProductToPlan, createPlan, detachProductFromPlan, type PlanPayload } from '../services/plans';
import { productKeys } from '@/features/products/hooks';
import type { Plan } from '../types/plan.types';

// Create succeeded, attach did not: the plan exists unattached and must not be
// reported as an outright failure.
export class PlanCreatedNotAttachedError extends Error {
    readonly plan: Plan;

    constructor(plan: Plan, options?: ErrorOptions) {
        super('Plan created but not attached.', options);
        this.name = 'PlanCreatedNotAttachedError';
        this.plan = plan;
    }
}

export function useProductPlanMutations(productId: string) {
    const queryClient = useQueryClient();

    const invalidate = () => queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });

    return {
        attachMutation: useMutation({
            mutationFn: ({ planId }: { planId: string }) => attachProductToPlan(planId, productId),
            onSuccess: invalidate,
        }),
        detachMutation: useMutation({
            mutationFn: ({ planId }: { planId: string }) => detachProductFromPlan(planId, productId),
            onSuccess: invalidate,
        }),
        createAndAttachMutation: useMutation({
            mutationFn: async (payload: PlanPayload) => {
                const plan = await createPlan(payload);
                try {
                    await attachProductToPlan(plan.id, productId);
                } catch (cause) {
                    throw new PlanCreatedNotAttachedError(plan, { cause });
                }
                return plan;
            },
            onSettled: () => {
                void invalidate();
                void queryClient.invalidateQueries({ queryKey: ['smr-admin', 'plan-search'] });
            },
        }),
    };
}
